import RecordedChampion from '../../../components/RecordedChampion.jsx'
import { splitParentSummary } from './splitParentSummary.js'
/* ========================================================================= *
 * THE CHAMPION CELL — the winner, its trust, its problems and its dossier,
 * in one place, with nothing to click first.
 *
 * Brian: "we need a column that actually shows the winner, instead of being
 * nested under the championship. the champion trust button is an additional
 * step, we should have the trust in the actual column. flag issues. opening
 * the full dossier should be in the column as well."
 *
 * WHAT WAS WRONG. Three separate faults that all had the same shape:
 *
 *   1. The champion was a `♛ Name` line near the BOTTOM of the card, below
 *      the owner and the incumbent. The single most important fact on the
 *      record — who won — was the least prominent thing on it.
 *
 *   2. Trust lived behind a floating "Champion trust" button that opened a
 *      panel. So the answer to "can I believe this?" was one click away from
 *      the thing it described, which in practice means nobody asks. A rep
 *      scanning fifty rows will not open fifty panels.
 *
 *   3. The dossier was reachable only by opening the row first.
 *
 * All three made the rep do work to see something the record already knows.
 *
 * WHY THIS NEEDS NO FETCH. The normalizer now carries `championSourceUrl`,
 * `championProvenance`, `championSearchTag` and the story. Trust is therefore
 * a PROPERTY OF THE ROW, computable locally and instantly — it does not need
 * the overlay layer, a second request, or a panel. That is the actual reason
 * the button could be deleted rather than merely moved.
 *
 * THE RULE IT KEEPS: a champion with no source is shown as UNSOURCED, loudly.
 * The whole point of the provenance work is that an unverified winner must
 * never look the same as a verified one.
 * ========================================================================= */

import { useState } from 'react'
import HuntNow from './HuntNow.jsx'
import { dateIntegrity, seedability, citationReuse } from '../../../lib/championships.js'
import styles from './pursuit.module.css'
import { athletesOf, titleTypeOf, schoolApplies, isMultiSchool, schoolGroups,
  championOfRecord } from '../../../lib/championDisplay.js'
import { crownProvenance } from './resolveModel.js'

/* THE FETCH BUTTON LIVED HERE.
 *
 * Brian: "we have fetch champion and hunt champion… what the fuck."
 *
 * It diagnosed what a record needed — an unplayed event refused rather than
 * searched, a company tie resolved on the spot, the exact facts a crowned
 * record still lacks. All of that was real and useful, and none of it justified
 * a SECOND button sitting directly above "Hunt champion" with a paragraph
 * between them.
 *
 * The diagnosis is now the first half of one press (see HuntNow): it runs, and
 * if the answer is "this needs a champion" the hunt follows immediately. It
 * only appears on screen when it IS the answer, rather than as something to
 * read before pressing the other button.
 */

/* ---- TRUST, decided from the record alone ------------------------------
 * Ordered most severe first. The first match wins, because a rep needs ONE
 * verdict, not a list of caveats — and the most severe thing true about a
 * record is the thing they need to act on.
 * ---------------------------------------------------------------------- */
export function championTrust(c) {
  /* ⭐ A SPLIT PARENT IS A CONTAINER, AND ITS VERDICT IS ITS FAMILY'S.
   * Brian, 2026-10-26: "when we just mark it a split into divisions - that
   * sure leaves alot left to be desired." The old branch below said "the
   * winners live on those" even when no division record existed (all four PIAA
   * golf parents, measured). So the verdict now reads the family: rows exist
   * and are complete → ok; anything unplaced → warn, naming it. The parent's
   * own stored champion is never the verdict, so it is never counted twice. */
  const fam = c?.splitFamily
  if (fam && fam.role === 'parent') {
    const rows = (fam.teams || []).length
    const indy = fam.indy ? fam.indy.athletes.length : 0
    const fixes = fam.fixes || []
    if (!rows && !fam.indy) {
      return { key: 'split_empty', label: 'Split rows unresolved in this view', tone: 'warn',
        why: fixes[0]?.reason || 'Marked as split, but its Team and Individual rows could not be resolved in this view.' }
    }
    const parts = [rows ? `${rows} team row${rows === 1 ? '' : 's'}` : '', fam.indy ? `indy ring (${indy})` : '']
      .filter(Boolean).join(' + ')
    return fixes.length
      ? { key: 'split_partial', label: `Split: ${parts}, ${fixes.length} to fix`, tone: 'warn',
        why: fixes.map((f) => f.reason).join(' ') }
      : { key: 'split_done', label: `Split: ${parts}`, tone: 'ok',
        why: 'Every team champion has its own row and every individual winner is on the one Individual (indy ring) grouping. This parent is a container and counts as no crown of its own.' }
  }
  const champ = (c?.champion || c?.winningSchool || '').trim()
  const src = (c?.championSourceUrl || '').trim()
  const prov = (c?.championProvenance || '').trim()
  const tag = (c?.championSearchTag || '').trim()

  if (!champ) {
    /* ⚠️ PURGATORY IS A REAL PLACE, AND THE BOARD HAS TO SHOW IT.
     *
     * Brian: "even if it's not a winner, it's worthy to tell my team, because
     * if not they will say the system doesn't work. Almost like a purgatory
     * stage until there is a confirmed winner."
     *
     * He is describing the exact failure this prevents. Every one of these
     * states used to render as "No champion yet", so a record we searched
     * this morning and a record nobody has ever looked at were indistinguishable
     * — and a team that cannot tell those apart concludes, correctly from what
     * they can see, that nothing is happening.
     *
     * The states already existed on the record (`sr_champion_search`,
     * `champion_searched_at`, `champion_search_attempts`). Nothing new is
     * stored here. The board was flattening seven answers into one.
     *
     * Every branch says WHEN it was looked at and WHAT HAPPENS NEXT, because
     * "not found" without a next step reads as failure, and "not found, retry
     * around the 3rd" reads as a system doing its job. */
    const at = (c?.championSearchedAt || '').slice(0, 10)
    const tries = Number(c?.championSearchAttempts || 0) || 0
    const when = at ? ` Last looked ${at}.` : ''

    if (tag === 'requested') {
      return { key: 'requested', label: 'Queued for the hunt', tone: 'info',
        why: 'Somebody on the team asked for this one. The hand hunt works requested records first, so it is next — not forgotten.' }
    }
    if (tag === 'not_yet_occurred') {
      return { key: 'not_yet', label: 'Has not happened yet', tone: 'quiet',
        why: `The event has not been contested. There is no champion to find, and searching would only turn up last season's result.${when}` }
    }
    if (tag === 'no_source_exists') {
      return { key: 'no_source', label: 'No source exists', tone: 'warn',
        why: `Searched, and this result is not published anywhere findable. Only a person who was there — a coach, a rep, the governing body — can settle it.${when}` }
    }
    if (tag === 'split_into_divisions') {
      return { key: 'split', label: 'Split into divisions', tone: 'info',
        why: 'This event crowns several champions. It has been broken into one record per division — the winners live on those, not here.' }
    }
    if (tag === 'not_found_human') {
      return { key: 'not_found', label: tries > 1 ? `Searched ${tries}×, not found` : 'Searched, not found', tone: 'warn',
        why: `We looked and could not establish a champion.${when} ${
          tries > 1
            ? 'Repeated failure usually means the result is not indexed, the record name is wrong, or it was never actually contested — stop searching and check the record.'
            : 'Often just too recent to be indexed. Worth another look in a few days.'
        }` }
    }
    /* The genuinely untouched ones. Said plainly rather than dressed up —
     * this is the only branch where "nobody has looked" is the truth. */
    return { key: 'none', label: 'No champion yet', tone: 'quiet',
      why: 'Nobody has looked yet, or the event has not happened. Press Find champion to put it in the queue.' }
  }

  if (!src) {
    return { key: 'unsourced', label: 'Unsourced', tone: 'bad',
      why: 'A champion with no source is a rumour. Do not put this in front of a customer until it has one.' }
  }
  if (prov === 'human_corrected') {
    return { key: 'corrected', label: 'Human corrected', tone: 'ok',
      why: 'A person replaced what was here. Their answer stands over anything automated.' }
  }
  if (prov === 'human_entered') {
    return { key: 'human', label: 'Entered by a person', tone: 'ok',
      why: 'Recorded by hand with a stated basis, not detected.' }
  }
  return { key: 'sourced', label: 'Sourced', tone: 'ok',
    why: 'Detected with a source URL on the record.' }
}

/* ---- ISSUES — say what is WRONG, not merely what is missing -------------
 * Each one is phrased as the consequence, because "no roster" means nothing
 * to a rep and "cannot personalise the ring" means something immediately.
 * ---------------------------------------------------------------------- */
export function championIssues(c) {
  const out = []

  /* ⚠️ A DATE PROBLEM IS AN ISSUE ON ITS OWN, BEFORE ANY CHAMPION EXISTS.
   *
   * Brian: "if a date is wrong, that will destroy credibility and we end up
   * missing it later on."
   *
   * This sits ABOVE the `if (!champ) return` below, and that placement is the
   * whole point — the two date faults that cost the most (an event 30+ days
   * past that nobody has looked at, and a date that was only ever an estimate)
   * happen on records with NO champion, which is exactly the set the old early
   * return dropped on the floor. A record can be silently wrong for a season
   * without ever having a winner to hang the warning on. */
  const di = dateIntegrity(c)
  if (di.key !== 'ok') {
    out.push({ key: di.key, label: di.label, severity: di.severity, why: di.why })
  }

  const champ = (c?.champion || '').trim()
  if (!champ) return out
  const story = c?.story || {}

  /* ⚠️ A SOURCELESS CROWN IS A WITHDRAWABLE CLAIM, AND THE FLAG NOW SAYS SO.
   *
   * The flag itself is unchanged — same key, same label, same `bad` severity,
   * so nothing that reads this list behaves differently. What it gained is the
   * `why`, and the reason it matters is that four wrong champions sat live for
   * a day: the board was flagging them correctly the whole time and the flag
   * read as a missing DETAIL rather than as a claim nothing supports. It is the
   * same rule the server's `sourceless-crown` predicate applies, phrased as the
   * consequence, and the drawer's Clear this crown control is what to do about
   * it. A record that is `champion_locked` keeps the flag — the claim really is
   * unsupported — and simply cannot be withdrawn; the control says that. */
  if (!c?.championSourceUrl) {
    out.push({
      key: 'nosource',
      label: 'no source',
      severity: 'bad',
      why: 'Nothing on this record supports the champion — no source URL, so there is no receipt for '
        + 'the claim. A crown with no source is not a crown: it cannot be trusted into artwork, and '
        + 'it can be withdrawn from the record drawer.',
    })
  }

  /* ⚠️ A SOURCE THAT EXISTS IS NOT A SOURCE THAT SAYS SO.
   *
   * `championTrust()` above answers "is there a URL", and a record with one
   * comes back `sourced` / tone `ok` — reassuring — whether or not that page
   * names this champion. The 2026-09-12 audit found 206 crowned records citing
   * 62 distinct URLs, and confirmed by reading the pages that 12 USEA records
   * cite a Beginner-Novice-only article and all 11 Festival of Champions
   * records cite a page carrying no results at all.
   *
   * ⚠️ `warn`, NEVER `bad`, AND THAT IS A RULING. Brian, 2026-09-12: "It
   * shouldn't be refused. It could have one source with multiple champs
   * listed." Sharing a source is legitimate — on the very article that exposed
   * this, 7 of the 19 records citing it are correctly sourced off it. Promoting
   * this to `bad` would put a blocking flag on those 7, and on the 22 vaulting
   * records that are also right. It raises a QUESTION; it does not assert a
   * defect. Pinned by verify-citation.mjs §6, which also asserts it reaches
   * neither the blocking rollup here nor HuntWorkspace's RING_BLOCKERS.
   *
   * Silent unless the universe has been through `attachCitationFanout`, so no
   * existing caller of `championIssues(record)` changes behaviour. */
  const shared = citationReuse(c)
  if (shared) out.push({ key: shared.key, label: shared.label, severity: 'warn', why: shared.why })

  /* ⚠️ SEEDABILITY IS A VERDICT, AND IT IS SEVERITY `bad` ON PURPOSE.
   *
   * Brian: "The crm must be able to seed." A record that cannot seed a ring is
   * not a record with four cosmetic gaps — it is a record that will waste a
   * designer's afternoon and, measured on the Michigan build, put the literal
   * word SURNAME on a proof.
   *
   * The four chips below (`notitle` / `nostory` / `noroster` / `nocoach`) are
   * the SAME underlying gaps, and they are deliberately kept: the rollup in the
   * component turns them into "needs N details", which is the right level of
   * noise for a record that can already be built and is merely thin. What was
   * missing is the one case where those gaps are not details at all. `bad`
   * severity is what keeps this OUT of that rollup and on the row by itself.
   *
   * It fires only when a ring genuinely cannot be seeded, so a clean row stays
   * clean — which is the rule that keeps every other flag meaning something. */
  const seed = seedability(c)
  if (seed.state === 'name_only' || (seed.state === 'partial' && seed.blocking.length)) {
    out.push({ key: 'noseed', label: seed.label, severity: 'bad', why: seed.why })
  }
  if (!story.titleLine) out.push({ key: 'notitle', label: 'no title wording', severity: 'warn' })
  if (!story.record && !story.score) out.push({ key: 'nostory', label: 'no result detail', severity: 'warn' })
  // Rings are personalised per player, so a missing roster is not cosmetic —
  // it is the thing that stops production.
  if (!story.rosterUrl) out.push({ key: 'noroster', label: 'no roster', severity: 'warn' })
  if (!story.coach) out.push({ key: 'nocoach', label: 'no coach', severity: 'quiet' })
  return out
}

/* `info` is the PURGATORY tone — the record is moving, it just has not
 * arrived. Deliberately not `warn`: "queued for the hunt" and "searched and
 * failed" are both non-answers, but only one of them is a problem, and
 * colouring them alike is what made the whole board read as broken. */
const TONE_CLASS = { ok: 'trustOk', warn: 'trustWarn', bad: 'trustBad', quiet: 'trustQuiet', info: 'trustInfo' }

/* ---- SHOWING A URL SO A PERSON CAN JUDGE IT ---------------------------
 * `www.` is dropped because it is four characters of nothing and this line
 * competes for width with everything else on the card.
 *
 * A malformed URL is shown RAW rather than swallowed. If something wrote
 * rubbish into the source field, the rep should see the rubbish — hiding it
 * behind a tidy fallback would make a broken record look like a fine one,
 * which is the same failure as an unsourced champion looking sourced.
 * ---------------------------------------------------------------------- */
export function sourceHost(url) {
  try { return new URL(url).hostname.replace(/^www\./i, '') }
  catch { return String(url || '').slice(0, 40) }
}

export function sourcePath(url, max = 34) {
  let p
  try { const u = new URL(url); p = u.pathname + u.search }
  catch { return '' }
  if (!p || p === '/') return ''
  // Truncate from the MIDDLE: the end of a path is usually the slug that says
  // which article this is, and chopping it off is what makes two sources from
  // the same site indistinguishable.
  if (p.length <= max) return p
  return `${p.slice(0, Math.ceil(max / 2) - 1)}…${p.slice(-Math.floor(max / 2))}`
}

/**
 * `onDossier` opens the Champion-to-Close panel directly from here, so the
 * rep never has to open the row first just to get at it.
 */
function ChampionCellDetails({ record, onDossier, compact = false, onAccepted }) {
  const c = record || {}
  const parent = splitParentSummary(c)
  if (parent) return <div className={styles.championCell}>
    <strong>{parent.headline}</strong><p>{parent.note}</p>
    {parent.reasons.map(reason => <p key={reason}>{reason}</p>)}
    {onDossier && <button type="button" className={styles.workOpen}
      onClick={event => { event.stopPropagation(); onDossier(c) }}>Review split results</button>}
  </div>
  /* The champion of record (a school on both kinds of title under the
   * 2026-10-19 ruling) and the athletes listed under it. `multiChampion` is
   * deliberately no longer consulted here — see the athletes block below for
   * why the overclaim flag was retired rather than rewired. */
  const athletes = athletesOf(c)
  const isIndividual = titleTypeOf(c) === 'individual'
  const schoolTitle = schoolApplies(c)
  const multiSchool = isMultiSchool(c)
  const groups = multiSchool ? schoolGroups(c) : null
  /* ⚠️ BLANK ON A MULTI-SCHOOL ROW, via championOfRecord. Reading
   * `c.champion || c.winningSchool` directly here would put whichever school
   * was stored first on the champion line and undo the ruling in the view. */
  /* A split PARENT shows its family, never its own stored name as "the"
   * champion — that is the double count Brian ruled out. */
  const fam = c.splitFamily && c.splitFamily.role === 'parent' ? c.splitFamily : null
  const champ = fam ? '' : championOfRecord(c)
  const trust = championTrust(c)
  const issues = championIssues(c)
  const prov = crownProvenance(c)

  return (
    <div className={styles.championCell}>
      <div className={styles.championTop}>
        {champ
          ? <span className={styles.championName} title={champ}>{champ}</span>
          : <span className={styles.championEmpty}>—</span>}

        {/* 🔴 WHO PUT THIS NAME HERE — PERMANENTLY, AND ON EVERY ROW.
          *
          * Brian: *"It must be visibly, permanently distinguishable as
          * rep-entered vs machine-resolved. A lane proved today by control
          * group that once provenance is unreadable NOTHING recovers it. Do
          * not create a second instance of that."*
          *
          * ⚠️ IT IS READ, NOT GUESSED. There is no `crowned_by` property, and
          * inventing one would be the second instance. What exists is
          * `champion_locked`, and it carries the answer for a structural
          * reason rather than by luck: `champion-update` answers 403
          * `automation_forbidden` to a service identity on that field, so only
          * a PERSON can set it. A locked crown is a human crown. A crown with
          * neither a lock nor a source reads as UNKNOWN — never as the
          * machine, because that would manufacture provenance rather than
          * report it. */}
        {prov && (
          <span
            className={`${styles.provChip} ${styles[`prov_${prov.key}`]}`}
            title={prov.why}
          >
            {prov.label}
          </span>
        )}

        {/* TRUST, INLINE. No button, no second request — the verdict is read
            straight off the record.

            It is still CLICKABLE, because the verdict and the evidence behind
            it are different questions. The chip answers "can I believe this?"
            at a glance; clicking opens the criteria checklist and provenance
            for this record specifically. What was removed is the floating
            button that opened a PORTFOLIO summary — a rep looking at one row
            wants that row, not a dataset-wide percentage. */}
        {/* ⭐ THE HIT AREA IS 44px; THE PILL IS STILL 24px.
          *
          * Brian: *"The 1485 need to get done."* Measured 2026-10-17 on the
          * live /command-center at 390px: 1,485 sub-44px targets, of which 982
          * are this chip — one per row. The previous lane did the
          * arithmetic on the obvious fix and was right to refuse it: growing a
          * 24px pill to 44px adds 20px per row, and on the densest surface in
          * the product that is thousands of pixels of phone scroll bought with
          * nothing but a metric.
          *
          * So the TARGET grows and the PILL does not. The outer span is the
          * control — it carries the role, the focus and the handlers, and it is
          * 44px tall because of `padding-block`. The negative `margin-block`
          * that goes with it cancels the padding out of the layout exactly, so
          * the row is the same height it was. The inner span keeps the
          * background, the border and the 24px pill, because the background
          * paints the padding box and a chip with 10px of padding would be a
          * 44px lozenge.
          *
          * ⚠️ TWO ELEMENTS RATHER THAN A ::after OVERLAY, AND THE REASON IS
          * MEASURABILITY. An absolutely-positioned pseudo-element would make
          * the chip genuinely tappable and leave its own box 24px — so every
          * harness in this repo, and every audit after it, would still read 24
          * and nobody could tell a real fix from a missing one. A fix that only
          * the author can see is not a fix. */}
        <span
          className={`${styles.trustHit} ${c.id ? styles.trustHitLive : ''}`}
          title={`${trust.why}${c.id ? '  (click for the evidence)' : ''}`}
          role={c.id ? 'button' : undefined}
          tabIndex={c.id ? 0 : undefined}
          onClick={(e) => {
            if (!c.id || typeof window.__srtOpen !== 'function') return
            e.stopPropagation()
            window.__srtOpen(c.id)
          }}
          onKeyDown={(e) => {
            if (!c.id || typeof window.__srtOpen !== 'function') return
            if (e.key !== 'Enter' && e.key !== ' ') return
            e.stopPropagation(); e.preventDefault()
            window.__srtOpen(c.id)
          }}
        >
          <span className={`${styles.trustChip} ${styles[TONE_CLASS[trust.tone]]}`}>
            {trust.label}
          </span>
        </span>
      </div>

      {/* THE SUMMARY — the champion's season in one line.
        *
        * Brian: "For a champion. This should be very easy, I want a summary per
        * champion included. You are already verifying, it should take another
        * 10 seconds to get this and have it live here."
        *
        * WHAT THIS REPLACED, and why it is an upgrade rather than a restyle.
        * This slot used to print three bare fragments side by side — `14-1`,
        * `28-21`, `over Oak Hill`. Every one of those is a real captured fact,
        * and together they still read as a stat block a rep has to assemble in
        * their head before they can say anything out loud. Worse, the slot only
        * ever showed three of the six facts the hunt captures: the VENUE, the
        * HEAD COACH and the exact TITLE WORDING were on the record the whole
        * time and rendered nowhere on the card.
        *
        * `championSummary()` (lib/championships.js) composes the same verified
        * facts into the sentence, so this is strictly more of what was already
        * captured, in the order somebody would actually say it. Nothing here
        * is written or inferred — see that function for why that matters.
        *
        * A crowned record with nothing behind the name renders NOTHING here
        * rather than an empty sentence; the issue flags below already say what
        * it needs, and repeating that as prose would just be noise. */}
      {/* ⚠️ THE SUMMARY RENDERS IN COMPACT TOO — it used to be gated on
        * `!compact`, and the main board passes `compact`, so the one line a rep
        * reads before they call was suppressed on EVERY row of the default
        * view. On a desk you could at least widen the column; on a phone it was
        * simply absent, and the only way to it was opening the drawer.
        *
        * Brian: "It was hidden behind layers when I wanted to see the summary."
        *
        * Compact should drop CHROME, not the substance. It stays clamped (2
        * lines, 3 on a phone) so a 427-row table is still scannable. */}
      {/* 🔴 THE ATHLETES, LISTED UNDER THE SCHOOL. Brian Kass, 2026-10-19:
        * "Individual championships are NOT split. The champion of record is
        * the SCHOOL, and the individual athlete and event are attached as the
        * reference."
        *
        * ⚠️ THIS REPLACES THE RED OVERCLAIM FLAG, WHICH BRIAN ASKED TO BE
        * RETIRED BY NAME: "Retire the red 'one name can't be all of them'
        * flag... so nothing keeps demanding splits."
        *
        * That flag was correct under the old model and is wrong under this
        * one. It fired whenever a multi-champion record carried any name, and
        * said the record was incomplete until it was split into children.
        * Under this ruling ONE record holding a school plus N athlete
        * references is the COMPLETE and correct shape — so the flag would now
        * mark every properly crowned individual title as broken, and send a
        * rep off to do a split the contract refuses to perform. A flag that
        * demands work the write path rejects is worse than no flag.
        *
        * What replaces it is not a warning but the DATA: the athletes the
        * crown actually carries. A record with athletes listed is visibly
        * complete; one with none is visibly a gap, without anybody being told
        * to split anything.
        *
        * ⚠️ `athletesError` IS NOT AN EMPTY LIST. Athletes recorded but
        * unreadable is a data defect and says so on the row; no athletes
        * recorded is a gap. Collapsing them would hide the defect. */}
      {/* ⭐ THE SPLIT FAMILY, SURFACED. Brian, 2026-10-26: "for the team
        * champion like La Salle they also get a location here that surfaces
        * them", and every individual winner "under the same tab". Each Team
        * row is its own record on the board (with its rep and CTAs); this
        * block is the map from the parent to them, plus the ONE Individual
        * (indy ring) tab listing every athlete with their school. A fix-it,
        * when there is one, carries its action as text the panel below acts
        * on — never a badge with nothing behind it. */}
      {fam && (
        <div className={styles.splitFamily}>
          {(fam.teams || []).map((t) => (
            <div key={t.id} className={styles.splitTeam}>
              <span className={styles.splitLabel}>{t.label}</span>
              <span className={styles.splitWinner}>{t.champion || 'No winner recorded yet'}</span>
            </div>
          ))}
          {fam.indy && (
            <div className={styles.splitIndy}>
              <div className={styles.splitLabel}>
                Individual grouping · {fam.indy.athletes.length} recorded winner{fam.indy.athletes.length === 1 ? '' : 's'}
                {fam.indy.schools.length ? ` · ${fam.indy.schools.length} school${fam.indy.schools.length === 1 ? '' : 's'}` : ''} · Review each actual record’s account and title gates
              </div>
              <ul className={styles.championAthletes}>
                {fam.indy.athletes.map((a, i) => (
                  <li key={`${a.recordId || ''}-${a.athlete}-${i}`}>
                    {a.athlete || 'Athlete not named yet'}
                    {a.school ? <span className={styles.athleteEvent}> — {a.school}</span> : null}
                    {a.event ? <span className={styles.athleteEvent}> · {a.event}</span> : null}
                  </li>
                ))}
              </ul>
            </div>
          )}
          {(fam.fixes || []).map((f) => (
            <div key={f.key} className={styles.splitFix} title={f.reason}>
              <b>{f.action}.</b> {f.reason}
            </div>
          ))}
        </div>
      )}

      {athletes && !athletes.ok && (
        <div className={styles.multiChamp} data-over="1"
          title={`champion_references could not be read: ${athletes.error}`}>
          Athletes recorded but unreadable — data defect, not a gap
        </div>
      )}
      {/* 🔴 MULTI-SCHOOL: NO SINGLE CHAMPION NAMED, EACH SCHOOL LISTED, CTA.
        * Brian Kass, 2026-10-19: "Multi-school records: name no single
        * champion. Render them flagged 'multi-school' with each school listed
        * and its athletes under it, plus a CTA to resolve. Don't guess."
        *
        * ⚠️ THE CHAMPION LINE ABOVE IS BLANK ON THESE ROWS, AND THAT IS THE
        * POINT. A wrestling meet won by six schools has no one account;
        * rendering whichever school was stored first would be the forbidden
        * guess arriving through the view instead of the write.
        *
        * ⚠️ AND IT MUST NOT READ AS A HUNT GAP. The dash above means "nobody
        * has found this yet"; this block means "found, and a person has to
        * pick the account". Two very different asks, so it says which, and
        * carries the action rather than being a dead-end flag. */}
      {multiSchool && groups?.ok && (
        <div className={styles.multiSchool}>
          <div className={styles.multiSchoolHead}>
            {/* An Individual (indy ring) grouping is MEANT to span schools —
              * Brian 2026-10-26: one indy ring for every individual winner,
              * each school its own account. Only a non-grouping record keeps
              * the "no single champion" warning. */}
            {c.splitFamily?.role === 'individual'
              ? `Individual (indy ring) — ${groups.groups.length} schools. One ring covers every athlete; each school is its own account to call.`
              : `Multi-school — ${groups.groups.length} schools won this title. No single champion of record.`}
          </div>
          {groups.groups.map((g) => (
            <div key={g.school} className={styles.multiSchoolGroup}>
              <div className={styles.multiSchoolName}>{g.school}</div>
              <ul className={styles.championAthletes}>
                {g.athletes.map((a, i) => (
                  <li key={`${a.athlete}-${a.event}-${i}`}>
                    {a.athlete}{a.event ? <span className={styles.athleteEvent}> — {a.event}</span> : null}
                  </li>
                ))}
              </ul>
            </div>
          ))}
          <button type="button" className={styles.multiSchoolCta}
            onClick={() => onDossier?.(record)}
            title="Several schools won this championship. Pick the school this record is sold to, or work each school separately.">
            Resolve the account →
          </button>
        </div>
      )}

      {!multiSchool && athletes?.ok && athletes.lines.length > 0 && (
        <ul className={styles.championAthletes}>
          {athletes.lines.map((a, i) => (
            <li key={`${a.athlete}-${a.event}-${i}`}>
              {a.athlete}{a.school ? <span className={styles.athleteEvent}> · {a.school}</span> : null}{a.event ? <span className={styles.athleteEvent}> — {a.event}</span> : null}
            </li>
          ))}
        </ul>
      )}
      {athletes?.ok && champ && isIndividual && schoolTitle && athletes.lines.length === 0 && (
        <div className={styles.multiChamp} data-over="0"
          title="The school is crowned; no athlete has been recorded against it yet.">
          No athlete recorded yet
        </div>
      )}

      {champ && c.summary?.text && (
        <div className={styles.championSummary} title={c.summary.text}>
          {c.summary.text}
        </div>
      )}

      {/* ISSUES, INLINE. Silent when there is nothing wrong — a row with a
          clean champion should look clean, or the flags stop meaning anything.

          WHY THE NON-BLOCKING ONES COLLAPSE. First cut printed every issue as
          its own flag. On the live page that put FOUR grey chips — "no title
          wording", "no result detail", "no roster", "no coach" — on nearly
          every crowned row. Four flags on every row is the same information as
          no flags on any row: nothing stands out, so nothing gets actioned,
          and the one that actually stops you ("no source") was sitting in the
          middle of the crowd wearing the same clothes.

          So: BLOCKING issues stay loud and individual. The rest become one
          chip that says how many details a ring still needs, with the list in
          the tooltip. The count is the useful part — "needs 4 details" tells a
          rep how far off buildable this is, which four does not. */}
      {issues.length > 0 && (() => {
        /* ⚠️ A DATE FAULT IS NOT "A DETAIL THE RING NEEDS".
         *
         * The rollup below collapses everything non-blocking into "needs N
         * details" and strips a leading "no " from each label for the tooltip.
         * Run a date issue through that and "Decided 1 day after the listed
         * date" is counted as a missing ring detail and listed as one — which
         * is both wrong and unfixable by the person reading it.
         *
         * Date issues are about whether the RECORD is right; ring issues are
         * about whether the PIECE can be built. Different problem, different
         * audience, so they are pulled out and shown on their own. */
        const isDate = (i) => String(i.key || '').startsWith('date_') || i.key === 'missed'
        const dates = issues.filter(isDate)
        const blocking = issues.filter((i) => !isDate(i) && i.severity === 'bad')
        const rest = issues.filter((i) => !isDate(i) && i.severity !== 'bad')
        return (
          <div className={styles.championIssues}>
            {dates.map((i) => (
              <span
                key={i.key}
                className={`${styles.issueFlag} ${styles[`issue_${i.severity === 'bad' ? 'bad' : i.severity === 'warn' ? 'warn' : 'info'}`]}`}
                title={i.why}
              >
                {i.label}
              </span>
            ))}
            {/* `title` added with the seedability verdict: "cannot seed a ring"
                is the headline, and the REASON (which field, and what it stops
                in production) is what makes it actionable rather than alarming.
                The older blocking flags carry no `why` and degrade to no
                tooltip, exactly as before. */}
            {blocking.map((i) => (
              <span key={i.key} className={`${styles.issueFlag} ${styles.issue_bad}`} title={i.why || undefined}>
                {i.label}
              </span>
            ))}
            {rest.length > 0 && (
              <span
                className={`${styles.issueFlag} ${styles.issue_quiet}`}
                title={`Missing for a personalised ring: ${rest.map((i) => i.label.replace(/^no /, '')).join(', ')}`}
              >
                needs {rest.length} detail{rest.length === 1 ? '' : 's'}
              </span>
            )}
          </div>
        )
      })()}

      {/* THE SOURCE URL, SHOWN — not hidden behind the word "receipt".
        *
        * Brian: "If the champion was sourced, we should include the url."
        *
        * He is right, and the reason is that THE DOMAIN IS ITSELF THE
        * EVIDENCE. `ncaa.com` and `someblog.wordpress.com` are not the same
        * proof, and a rep deciding whether to put this in front of a customer
        * needs to see which one it is without hovering, clicking, or opening
        * anything. A link labelled "receipt" tells them a source exists; it
        * does not tell them whether the source is any good.
        *
        * The domain leads because that is the part that carries the judgement.
        * The path follows, truncated, because it distinguishes one article
        * from another on the same site. The full URL is on the anchor and in
        * the tooltip, so nothing is lost. */}
      {c.championSourceUrl && (
        <div className={styles.championSource}>
          {/* Same two-element hit area as the trust chip above, and for the
            * same measured reason: 465 of the 1,485 sub-44px targets are this
            * link. The <a> is 44px tall and contributes its old 20px to the
            * layout; the inner span carries the tinted pill. */}
          <a
            className={styles.championSourceHit}
            href={c.championSourceUrl}
            target="_blank"
            rel="noreferrer"
            onClick={(e) => e.stopPropagation()}
            title={c.championSourceUrl}
          >
            <span className={styles.championSourceLink}>
              <span className={styles.sourceHost}>{sourceHost(c.championSourceUrl)}</span>
              <span className={styles.sourcePath}>{sourcePath(c.championSourceUrl)}</span>
              <span className={styles.sourceArrow}>↗</span>
            </span>
          </a>
        </div>
      )}

      {/* ONE BUTTON, AND IT STAYS IN THE APP.
        *
        * Brian: "we should be able to pushbutton it and pull and record."
        * Then: "we have fetch champion and hunt champion… what the fuck."
        *
        * Both were fair. This started as a ChatGPT deep-link that recorded
        * nothing, became a real in-app hunt, and then sat directly beneath a
        * separate "Fetch champion" that diagnosed the same record — two
        * near-identical controls with a paragraph of text between them.
        *
        * Now it is one press: it diagnoses first (an unplayed event is refused
        * before a search is spent), then hunts, shows the source, and Record
        * writes it. The diagnosis only surfaces when it IS the answer. */}
      {/* A COMPLETE split parent is a container: "Find champion" on it would
        * send a rep hunting for a winner that lives on its rows. The hunt and
        * record-the-divisions controls stay whenever the family has a fix-it,
        * because that is exactly the work they do. */}
      {!(fam && !(fam.fixes || []).length) && (
        <HuntNow record={c} compact={compact} onAccepted={onAccepted} />
      )}

      <div className={styles.championActions}>
        {onDossier && (
          <button
            type="button"
            className={styles.championDossier}
            onClick={(e) => { e.stopPropagation(); onDossier(c) }}
            title="Open the full dossier — brand kit, ring surfaces, price, campaign"
          >
            Full dossier →
          </button>
        )}
      </div>
    </div>
  )
}

export default function ChampionCell(props) {
 const c = props.record || {}
 const parent = splitParentSummary(c)
 const groups = isMultiSchool(c) ? schoolGroups(c) : null
 // The detail view owns the primary name and structured athlete references.
 // The supplement preserves different stored claims, warnings and next work.
 const displayedClaims = parent ? [] : [championOfRecord(c), ...(groups?.ok ? groups.groups.map(group => group.school) : [])].filter(Boolean)
 const memberText = member => [member.athlete,member.school,member.event].filter(value => typeof value === 'string' && value.trim()).map(value => value.trim()).join(' · ')
 const athletes = athletesOf(c)
 const displayedMembers = parent ? [] : groups?.ok
   ? groups.groups.flatMap(group => (group.athletes || []).map(member => memberText({...member,school:group.school})))
   : !isMultiSchool(c) && athletes.ok ? athletes.lines.map(memberText) : []
 return <div data-recorded-champion><RecordedChampion record={c} displayedClaims={displayedClaims} displayedMembers={displayedMembers} /><ChampionCellDetails {...props} /></div>
}
